import { faBank, faTags } from '@fortawesome/pro-regular-svg-icons'
import { Button, Icon, Text, Toggle } from '@/ui'
import { TextColors, TypographyStyles } from '@/styles'
import { FinancialAccountPrimaryUse } from '@/reducers/finances/financialAccountsReducer'
import type { PlaidItem } from '@/reducers/finances/plaidItemReducer'
import type { PrimaryUseAnswer } from '@/features/onboarding/connect-stage/types'
import { InstitutionLogo } from '@/features/user/settings/sections/connected-financial-institutions-card/institution-card/logo'
import { CardIcon } from './cardParts'
import { WidgetCard } from './WidgetCard'
import {
  FOLLOW_UP_QUESTIONS,
  isEveryAnswerComplete,
  toUsageCount,
  type AccountUsageAnswers,
} from './accountUsage'

export interface AccountUsageRow {
  id: number
  name: string
  detail: string
  institution: PlaidItem | null
}

interface AccountUsageCardProps {
  accounts: AccountUsageRow[]
  answers: AccountUsageAnswers
  saving: boolean
  saveFailed: boolean
  onAnswer: (accountId: number, answer: PrimaryUseAnswer) => void
  onConfirm: () => void
}

const USES = [
  { value: FinancialAccountPrimaryUse.BUSINESS, label: 'Business' },
  { value: FinancialAccountPrimaryUse.PERSONAL, label: 'Personal' },
]

const YES_NO = [
  { value: true, label: 'Yes' },
  { value: false, label: 'No' },
]

const UNANSWERED: PrimaryUseAnswer = { primaryUse: null, hasOtherType: null }

const AccountTile = ({ institution }: { institution: PlaidItem | null }) =>
  institution ? (
    <InstitutionLogo
      institution={institution}
      width={32}
      className="flex size-8 shrink-0 items-center justify-center rounded-full bg-neutral-300"
    />
  ) : (
    <div
      aria-hidden
      className="flex size-8 shrink-0 items-center justify-center rounded-full bg-neutral-50"
    >
      <Icon icon={faBank} className="text-xs text-neutral-500" />
    </div>
  )

export const AccountUsageCard = ({
  accounts,
  answers,
  saving,
  saveFailed,
  onAnswer,
  onConfirm,
}: AccountUsageCardProps) => {
  const complete = isEveryAnswerComplete(
    accounts.map((account) => account.id),
    answers
  )

  return (
    <WidgetCard>
      <div className="flex items-start gap-3">
        <CardIcon icon={faTags} />
        <div className="flex flex-col">
          <Text variant={TypographyStyles.LABEL_MD}>
            How do you use each account?
          </Text>
          <Text variant={TypographyStyles.BODY_XS} color={TextColors.MUTED}>
            {toUsageCount(accounts.map(({ id }) => answers[id] ?? UNANSWERED))}
          </Text>
        </div>
      </div>
      <ul className="flex flex-col">
        {accounts.map((account) => {
          const answer = answers[account.id] ?? UNANSWERED
          return (
            <li
              key={account.id}
              className="flex flex-col gap-3 border-t border-neutral-50 py-4 first:border-t-0 first:pt-0"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex min-w-0 items-start gap-3">
                  <AccountTile institution={account.institution} />
                  <div className="flex min-w-0 flex-col">
                    <Text variant={TypographyStyles.BODY_SM}>
                      {account.name}
                    </Text>
                    <Text
                      variant={TypographyStyles.BODY_XS}
                      color={TextColors.MUTED}
                    >
                      {account.detail}
                    </Text>
                  </div>
                </div>
                <div
                  role="group"
                  aria-label={`How you use ${account.name}`}
                  className="flex shrink-0 gap-2"
                >
                  {USES.map((use) => (
                    <Toggle
                      key={use.value}
                      variant="outline"
                      size="sm"
                      className="body-sm"
                      checked={answer.primaryUse === use.value}
                      disabled={saving}
                      onCheckedChange={() =>
                        onAnswer(account.id, {
                          primaryUse: use.value,
                          hasOtherType:
                            answer.primaryUse === use.value
                              ? answer.hasOtherType
                              : null,
                        })
                      }
                    >
                      {use.label}
                    </Toggle>
                  ))}
                </div>
              </div>
              {answer.primaryUse && (
                <div className="flex items-center justify-between gap-3">
                  <Text
                    variant={TypographyStyles.BODY_XS}
                    className="text-neutral-600"
                  >
                    {FOLLOW_UP_QUESTIONS[answer.primaryUse]}
                  </Text>
                  <div
                    role="group"
                    aria-label={FOLLOW_UP_QUESTIONS[answer.primaryUse]}
                    className="flex shrink-0 gap-2"
                  >
                    {YES_NO.map((option) => (
                      <Toggle
                        key={option.label}
                        variant="outline"
                        size="sm"
                        className="body-sm"
                        checked={answer.hasOtherType === option.value}
                        disabled={saving}
                        onCheckedChange={() =>
                          onAnswer(account.id, {
                            ...answer,
                            hasOtherType: option.value,
                          })
                        }
                      >
                        {option.label}
                      </Toggle>
                    ))}
                  </div>
                </div>
              )}
            </li>
          )
        })}
      </ul>
      {saveFailed && (
        <Text variant={TypographyStyles.BODY_XS} color={TextColors.DANGER}>
          {"Couldn't save that. Try again."}
        </Text>
      )}
      <Button
        size="sm"
        variant="primary"
        className="w-fit"
        disabled={!complete || saving}
        loading={saving}
        onClick={onConfirm}
      >
        {"That's right"}
      </Button>
    </WidgetCard>
  )
}
